import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { LabsApiActions } from '../actions';
import { LabsUiActions } from '../actions';
import { catchError, EMPTY, filter, from, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { LabService } from '../../services/lab.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { ProjectsApiActions, ProjectsUiActions } from '../../../projects/state/actions';
import { LabFacade } from '../../facade/lab.facade';
import { UserMgmtFacade } from '../../../user-management/facade/user-mgmt.facade';
import { UserMgmtUiActions } from '../../../user-management/state/actions';
import { ProjectFacade } from '../../../projects/facades/project.facade';

// DRF refuses a lifecycle action with {detail}, a bad archive note with
// {archive_note: [...]} and a bad justification with {justification: [...]}.
// Fall back to the transport error otherwise.
function lifecycleErrorMessage(error: {
  error?: { detail?: string; archive_note?: string[]; justification?: string[] };
  message?: string;
}): string {
  return (
    error?.error?.detail ||
    error?.error?.archive_note?.[0] ||
    error?.error?.justification?.[0] ||
    error?.message ||
    String(error)
  );
}

@Injectable()
export class LabsEffects {
  private actions$ = inject(Actions);
  private labService = inject(LabService);
  private toastService = inject(ToastService);
  private labFacade = inject(LabFacade);
  private userMgmtFacade = inject(UserMgmtFacade);
  private projectFacade = inject(ProjectFacade);
  private router = inject(Router);
  selectedLab = this.labFacade.getLabDetailSignal();

  getLabs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.GetLabs),
      mergeMap(() =>
        this.labService.getLabs().pipe(
          map((labs) => LabsApiActions.GetLabsSuccess({ payload: labs })),
          catchError((error) => {
            this.toastService.show(`Error fetching labs: ${error.message || error}`, 'error');
            return of(LabsApiActions.GetLabsFailure({ errors: error }));
          })
        )
      )
    )
  );

  getLabDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.GetLabDetail),
      // Guard against invalid lab IDs (e.g. 0 from a `?? 0` fallback in
      // another effect when the selected-lab signal hadn't loaded yet).
      // Hitting /api/labs/0/ would always 404.
      filter(({ labId }) => Number.isFinite(labId) && labId > 0),
      // switchMap cancels any in-flight GetLabDetail when a newer one is
      // dispatched, so a fetch for the lab the user just left can never land
      // on top of the one they navigated to. LabShellComponent owns the
      // route-driven fetch and the rest are post-mutation refreshes
      switchMap(({ labId }) =>
        this.labService.getLabDetail(labId).pipe(
          map((lab) => LabsApiActions.GetLabDetailSuccess({ payload: lab })),
          catchError((error) => {
            this.toastService.show(`Error fetching lab detail: ${error.message || error}`, 'error');
            return of(LabsApiActions.GetLabDetailFailure({ errors: error }));
          })
        )
      )
    )
  );

  postLab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.PostLab),
      mergeMap(({ lab }) =>
        this.labService.postLab(lab).pipe(
          map(() => LabsApiActions.PostLabSuccess({ payload: lab })),
          catchError((error) => {
            this.toastService.show(`Error creating lab: ${error.message || error}`, 'error');
            return of(LabsApiActions.PostLabFailure({ errors: error }));
          })
        )
      )
    )
  );

  getLabProjectUsersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.GetLabProjectUsersList),
      // Guard against invalid lab IDs — these would 404 on
      // /api/labs/0/members/ (or similar) and just create user-visible noise.
      filter(({ labId }) => Number.isFinite(labId) && labId > 0),
      // switchMap so a fetch for a newly-selected lab cancels the in-flight
      // fetch for the previously-selected lab.
      switchMap(({ labId }) =>
        this.labService.getLabProjectUsersList(labId).pipe(
          map((users) => LabsApiActions.GetLabProjectUsersListSuccess({ payload: users })),
          catchError((error) => {
            this.toastService.show(`Error fetching lab project users: ${error.message || error}`, 'error');
            return of(LabsApiActions.GetLabProjectUsersListFailure({ errors: error }));
          })
        )
      )
    )
  );

  patchLab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.PatchLab),
      mergeMap(({ lab }) =>
        this.labService.patchLab(lab).pipe(
          map(() => LabsApiActions.PatchLabSuccess({ payload: lab })),
          catchError((error) => {
            this.toastService.show(`Error updating lab: ${error.message || error}`, 'error');
            return of(LabsApiActions.PatchLabFailure({ errors: error }));
          })
        )
      )
    )
  );

  postLabUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.PostAssignLabUser),
      mergeMap(({ labUser }) =>
        this.labService.postAssignLabUser(labUser).pipe(
          map(() => LabsApiActions.PostAssignLabUserSuccess({ payload: labUser })),
          catchError((error) => {
            this.toastService.show(`Error assigning lab user: ${error.message || error}`, 'error');
            return of(LabsApiActions.PostAssignLabUserFailure({ errors: error }));
          })
        )
      )
    )
  );

  refreshLabsAfterCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsApiActions.PostLabSuccess),
      tap(() => {
        this.toastService.show('Lab creation successful!', 'success');
      }),
      map(() => LabsUiActions.GetLabs())
    )
  );

  updateLabProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsApiActions.postProjectSuccess),
      tap(() => {
        this.toastService.show('Project creation successful!', 'success');
      }),
      mergeMap(({ payload }) => {
        // Always refresh the labs list so the new project shows up there.
        const actions: Action[] = [LabsUiActions.GetLabs()];

        // Only refresh the lab detail / project users if the user is still
        // viewing the lab the project was created in. Otherwise we'd
        // overwrite the currently-selected lab with stale data and trigger a
        // cascade of follow-up calls for a lab the user has already left.
        //
        // We check selectedLabSignal (synchronously updated when the user
        // clicks a lab) rather than getLabDetailSignal (only updated after
        // the GET /labs/:id response), since the post-project response can
        // arrive after the user has clicked away but before the new lab's
        // detail has loaded.
        // Platform admins browsing a lab have no selection at all, so fall back
        // to the loaded lab detail for them.
        const currentLab = this.labFacade.selectedLabSignal() ?? this.labFacade.getLabDetailSignal();
        if (currentLab?.id === payload.lab) {
          actions.push(
            LabsUiActions.GetLabDetail({ labId: payload.lab }),
            LabsUiActions.GetLabProjectUsersList({ labId: payload.lab })
          );
        }

        return actions;
      })
    )
  );

  // Note: refreshLabProjectUsersAfterUpdate$ / refreshLabAfterPostProjectUser$
  // were removed — the same actions (patchProjectUserSuccess /
  // postProjectUserSuccess) are already handled in ProjectsEffects, which
  // dispatches GetLabProjectUsersList for us. Keeping both caused duplicate
  // /api/labs/:id/members/ requests for every project-user mutation.

  refreshLabProjectUsersAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsApiActions.deleteProjectUserSuccess),
      // No-op if we don't currently have a lab loaded — throwing inside an
      // effect tears down the entire effect stream.
      // The lab on screen, which the lab shell fetches from the route on every
      // lab page. A platform admin can be switched into one lab while viewing
      // another, so the loaded detail wins over their selected context.
      map(() => this.labFacade.getLabDetailSignal()?.id ?? this.labFacade.selectedLabSignal()?.id),
      filter((labId): labId is number => Number.isFinite(labId) && (labId ?? 0) > 0),
      map((labId) => LabsUiActions.GetLabProjectUsersList({ labId }))
    )
  );

  refreshLabAfterPatchUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsApiActions.PatchLabSuccess),
      tap(() => {
        this.toastService.show('Lab update successful!', 'success');
      }),
      mergeMap(() => {
        // Refresh the lab that was just edited (the one on screen), not the lab
        // an admin happens to have switched into.
        const labId = this.labFacade.getLabDetailSignal()?.id ?? this.labFacade.selectedLabSignal()?.id;
        if (!labId || labId <= 0) {
          return EMPTY;
        }
        return from([LabsUiActions.GetLabDetail({ labId }), LabsUiActions.GetLabProjectUsersList({ labId })]);
      })
    )
  );

  refreshLabAfterPostLabUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsApiActions.PostAssignLabUserSuccess),
      tap(() => {
        this.toastService.show('Lab user assignment successful!', 'success');
      }),
      mergeMap(() => {
        const selectedUser = this.userMgmtFacade.selectedUserSignal();
        const selectedLabId = this.labFacade.getLabDetailSignal()?.id ?? this.labFacade.selectedLabSignal()?.id;
        const selectedProject = this.projectFacade.projectSelectedSignal();
        const selectedProjectUser = this.projectFacade.selectedProjectUserSignal();

        // Get project ID from selectedProjectUser (if updating permissions) or selectedProject
        const projectId = selectedProjectUser?.project?.id ?? selectedProject?.id ?? 0;

        const actions: Action[] = [];

        if (selectedLabId && selectedLabId > 0) {
          actions.push(LabsUiActions.GetLabProjectUsersList({ labId: selectedLabId }));
        }

        // Refresh project details if we have a project ID
        if (projectId > 0) {
          actions.push(ProjectsUiActions.GetProjectDetails({ projectId }));
        }

        // Refresh user if selected
        if (selectedUser?.id) {
          actions.push(UserMgmtUiActions.GetUser({ id: selectedUser.id.toString() }));
        }

        return from(actions);
      })
    )
  );

  getArchivedLabs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.GetArchivedLabs),
      switchMap(() =>
        this.labService.getLabsIncludingArchived().pipe(
          map((labs) => LabsApiActions.GetArchivedLabsSuccess({ payload: labs })),
          catchError((error) => {
            this.toastService.show(`Error fetching archived labs: ${lifecycleErrorMessage(error)}`, 'error');
            return of(LabsApiActions.GetArchivedLabsFailure({ errors: error }));
          })
        )
      )
    )
  );

  deactivateLab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.DeactivateLab),
      mergeMap(({ labId }) =>
        this.labService.deactivateLab(labId).pipe(
          tap(() => this.toastService.show('Lab deactivated.', 'success')),
          map(() => LabsApiActions.DeactivateLabSuccess({ labId })),
          catchError((error) => {
            this.toastService.show(`Error deactivating lab: ${lifecycleErrorMessage(error)}`, 'error');
            return of(LabsApiActions.DeactivateLabFailure({ errors: error }));
          })
        )
      )
    )
  );

  reactivateLab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.ReactivateLab),
      mergeMap(({ labId }) =>
        this.labService.reactivateLab(labId).pipe(
          tap(() => this.toastService.show('Lab reactivated.', 'success')),
          map(() => LabsApiActions.ReactivateLabSuccess({ labId })),
          catchError((error) => {
            this.toastService.show(`Error reactivating lab: ${lifecycleErrorMessage(error)}`, 'error');
            return of(LabsApiActions.ReactivateLabFailure({ errors: error }));
          })
        )
      )
    )
  );

  archiveLab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.ArchiveLab),
      mergeMap(({ labId, archiveNote }) =>
        this.labService.archiveLab(labId, archiveNote).pipe(
          tap(() => this.toastService.show('Lab archiving started. Its GCP resources are being torn down.', 'success')),
          map(() => LabsApiActions.ArchiveLabSuccess({ labId })),
          catchError((error) => {
            this.toastService.show(`Error archiving lab: ${lifecycleErrorMessage(error)}`, 'error');
            return of(LabsApiActions.ArchiveLabFailure({ errors: error }));
          })
        )
      )
    )
  );

  deleteLab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.DeleteLab),
      mergeMap(({ labId }) =>
        this.labService.deleteLab(labId).pipe(
          tap(() =>
            this.toastService.show(
              'Lab deletion started. The lab is removed once its GCP resources are gone.',
              'success'
            )
          ),
          map(() => LabsApiActions.DeleteLabSuccess({ labId })),
          catchError((error) => {
            this.toastService.show(`Error deleting lab: ${lifecycleErrorMessage(error)}`, 'error');
            return of(LabsApiActions.DeleteLabFailure({ errors: error }));
          })
        )
      )
    )
  );

  retryLabTeardown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.RetryLabTeardown),
      mergeMap(({ labId }) =>
        this.labService.retryLabTeardown(labId).pipe(
          tap(() => this.toastService.show('Lab teardown restarted.', 'success')),
          map(() => LabsApiActions.RetryLabTeardownSuccess({ labId })),
          catchError((error) => {
            this.toastService.show(`Error retrying lab teardown: ${lifecycleErrorMessage(error)}`, 'error');
            return of(LabsApiActions.RetryLabTeardownFailure({ errors: error }));
          })
        )
      )
    )
  );

  requestLabArchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.RequestLabArchive),
      mergeMap(({ labId, justification }) =>
        this.labService.requestLabArchive(labId, justification).pipe(
          tap(() => this.toastService.show('Archive request sent to the platform admins for review.', 'success')),
          map(() => LabsApiActions.RequestLabArchiveSuccess({ labId })),
          catchError((error) => {
            this.toastService.show(`Error requesting lab archive: ${lifecycleErrorMessage(error)}`, 'error');
            return of(LabsApiActions.RequestLabArchiveFailure({ errors: error }));
          })
        )
      )
    )
  );

  // The lab detail carries the pending request, so refetch it.
  refreshLabAfterArchiveRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsApiActions.RequestLabArchiveSuccess),
      map(({ labId }) => LabsUiActions.GetLabDetail({ labId }))
    )
  );

  // Refetch the lab and both lab lists so the status badge, the lifecycle
  // controls and the Labs page all reflect the new state. The toasts are
  // shown by the action effects above.
  refreshLabAfterLifecycleChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LabsApiActions.DeactivateLabSuccess,
        LabsApiActions.ReactivateLabSuccess,
        LabsApiActions.ArchiveLabSuccess,
        LabsApiActions.RetryLabTeardownSuccess
      ),
      mergeMap(({ labId }) =>
        from([LabsUiActions.GetLabDetail({ labId }), LabsUiActions.GetLabs(), LabsUiActions.GetArchivedLabs()])
      )
    )
  );

  // The row can be gone by the time the response lands, so leave the lab page
  // and refresh only the lists; a detail refetch would 404.
  afterLabDeleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsApiActions.DeleteLabSuccess),
      tap(() => this.router.navigate(['/labs'])),
      mergeMap(() => from([LabsUiActions.GetLabs(), LabsUiActions.GetArchivedLabs()]))
    )
  );

  setLabAutoApproval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.SetLabAutoApproval),
      mergeMap(({ update }) =>
        this.labService.setLabAutoApproval(update).pipe(
          tap(() => {
            this.toastService.show('Auto-approval updated.', 'success');
          }),
          map(() => LabsUiActions.GetLabProjectUsersList({ labId: update.lab_id })),
          catchError((error) => {
            this.toastService.show(
              `Error updating auto-approval: ${error?.error?.error || error.message || error}`,
              'error'
            );
            return of();
          })
        )
      )
    )
  );

  deleteLabUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabsUiActions.DeleteLabUser),
      mergeMap(({ labId, userId }) =>
        this.labService.deleteLabUser(labId, userId).pipe(
          tap(() => {
            this.toastService.show('Lab user removed successfully!', 'success');
          }),
          mergeMap(() => {
            const actions: Action[] = [LabsUiActions.GetLabProjectUsersList({ labId })];
            // Removing yourself changes your own role gates, so the cached `me`
            // record has to be refetched alongside the roster.
            if (this.isCurrentUser(userId)) {
              actions.push(UserMgmtUiActions.GetMeUser());
            }
            return from(actions);
          }),
          catchError((error) => {
            this.toastService.show(`Error deleting lab user: ${error.message || error}`, 'error');
            return of();
          })
        )
      )
    )
  );

  // Every director-only control on the lab page (Actions column, Assign User,
  // the Manage User modal, per-row edit/delete icons) is gated on the cached
  // `me` record, which is otherwise only refetched on login and on route
  // changes. Without this, changing your own role leaves those controls on
  // screen until a reload, so demoting yourself still looks like you can
  // promote yourself back.
  refreshMeAfterOwnMembershipChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LabsApiActions.PostAssignLabUserSuccess,
        ProjectsApiActions.postProjectUserSuccess,
        ProjectsApiActions.patchProjectUserSuccess
      ),
      filter((action) =>
        this.isCurrentUser(
          action.type === LabsApiActions.PostAssignLabUserSuccess.type ? action.payload.user_id : action.payload.user
        )
      ),
      map(() => UserMgmtUiActions.GetMeUser())
    )
  );

  private isCurrentUser(userId: number | undefined): boolean {
    const meId = this.userMgmtFacade.getMeUserSignal()?.id;
    return !!meId && !!userId && meId === userId;
  }
}
